/** @jsx jsx */
import React, { useCallback } from 'react';
import { styled, jsx } from '@compiled/react';
import { Flex, xcss } from '@atlaskit/primitives';
import CommentIcon from '@atlaskit/icon/core/comment';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';
import { useConversationAssistantSidebarApi } from '@atlassian/jira-conversation-assistant/src/ui/useConversationAssistantSidebarApi.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const ExplainWorkflowButton = ({ onClick }: Props) => {
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const publish = usePublish('ai-mate');
	const { isSidebarOpen } = useConversationAssistantSidebarApi({});
	const isRovoChatOpen = isSidebarOpen();

	const onClickHandler = useCallback(() => {
		publish({
			type: 'chat-new',
			product: 'jira',
			openChat: !isRovoChatOpen,
			source: 'jira-work-item-status-field',
			data: {
				dialogues: [],
				prompt: `Explain the workflow for ${issueKey}`,
			},
		});
		onClick();
	}, [isRovoChatOpen, issueKey, onClick, publish]);

	return (
		<Flex xcss={flexContainerStyles} alignItems="start">
			<StyledButton
				appearance="subtle"
				shouldFitContainer
				onClick={onClickHandler}
				iconBefore={<CommentIcon label="" />}
			>
				{formatMessage(messages.explainWorkflow)}
			</StyledButton>
		</Flex>
	);
};

// Move all styled/xcss/css components to the bottom
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const StyledButton = styled(Button)({
	alignItems: 'center',
	textAlign: 'left',
	color: token('color.text'),
	paddingTop: token('space.200'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.150'),
	height: '100%',
});

const flexContainerStyles = xcss({
	flexGrow: 1,
	height: token('space.500'),
});
